// TransactionList component displays a list of transactions with filtering and modal details.
//
// Usage:
//   - Admin: <TransactionList /> (shows date presets, year selection, and custom ranges)
//   - POS/Cashier: <TransactionList restrictToToday={true} /> (shows only today's transactions, no date filter bar)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../services/axiosInstance";
import EmptyState from "../CommonComponents/EmptyState";
import FilterBar from "../CommonComponents/FilterBar";
import { ListOrdered, Grid, List, Printer } from "lucide-react";
import TransactionCard from "./TransactionCard";
import Modal from "../CommonComponents/TransactionModal";
import ConfirmationModal from "../CommonComponents/ConfirmationModal";
import { fetchBranches } from "../../services/branchApi";
import { fetchUsers } from "../../services/usersApi";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { printReceipt } from "../../utils/receiptPrinter";

const APP_TIMEZONE = "Asia/Manila";
const VOID_AUTHORIZED_ROLES = new Set(["cashier", "manager", "supervisor", "all_staff"]);

const normalizeVoidRole = (role) => {
    const normalized = String(role || "").trim().toLowerCase();
    if (normalized === "staff") return "all_staff";
    return VOID_AUTHORIZED_ROLES.has(normalized) ? normalized : "manager";
};

const getVoidRoleLabel = (role) => {
    const normalized = normalizeVoidRole(role);
    if (normalized === "all_staff") return "all staff";
    return normalized;
};

const getUserRoleName = (targetUser) => {
    if (Number(targetUser?.user_type) === 1) return "admin";
    return String(targetUser?.role_name || targetUser?.role || targetUser?.roleDetail?.name || "").trim().toLowerCase();
};

const userMatchesVoidPolicy = (targetUser, policy) => {
    const roleName = getUserRoleName(targetUser);
    if (policy === "all_staff") return ["cashier", "manager", "supervisor", "staff"].includes(roleName);
    return roleName === policy;
};

const isCashPayment = (tx) => {
    const method = String(tx?.payment_method || "").trim().toLowerCase();
    const collectionMode = String(tx?.payment_collection_mode || "").trim().toUpperCase();
    return method === "cash" || collectionMode === "CASH";
};

const formatDateOnlyUtc = (date) => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatDateInTimeZone = (date, timeZone = APP_TIMEZONE) => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);
    const year = parts.find((p) => p.type === "year")?.value;
    const month = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    return `${year}-${month}-${day}`;
};

const shiftDateOnlyByDaysUtc = (dateOnly, days) => {
    const d = new Date(`${dateOnly}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() - days);
    return formatDateOnlyUtc(d);
};

const formatDisplayDate = (dateOnly) => {
    if (!dateOnly) return "-";
    const date = new Date(`${dateOnly}T00:00:00`);
    if (Number.isNaN(date.getTime())) return dateOnly;
    return date.toLocaleDateString();
};

const StockBadge = ({ value, tone, title, prefix = "" }) => {
    const toneClasses = {
        beginning: "bg-sky-50 text-sky-700 ring-sky-200",
        added: "bg-emerald-50 text-emerald-700 ring-emerald-200",
        ending: "bg-indigo-50 text-indigo-700 ring-indigo-200",
        sold: "bg-amber-50 text-amber-700 ring-amber-200",
    };

    return (
        <span
            className={`inline-flex min-w-[3rem] items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums ring-1 ${toneClasses[tone] || "bg-gray-50 text-gray-700 ring-gray-200"}`}
            title={title}
        >
            {prefix}{Number(value || 0).toLocaleString()}
        </span>
    );
};

const formatCurrency = (value) => `₱${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})}`;

const getManilaDateRange = ({ dateFilter, customStartDate, customEndDate, selectedYear, selectedDate }) => {
    if (dateFilter === "custom") {
        return {
            startDate: customStartDate || "",
            endDate: customEndDate || "",
        };
    }

    if (dateFilter === "selectDate") {
        return {
            startDate: selectedDate || "",
            endDate: selectedDate || "",
        };
    }

    const now = new Date();
    const todayManila = formatDateInTimeZone(now, APP_TIMEZONE);

    if (dateFilter === "year" || dateFilter === "selectYear") {
        const year = dateFilter === "year" ? todayManila.slice(0, 4) : selectedYear;
        return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
    }

    if (dateFilter === "today") {
        return { startDate: todayManila, endDate: todayManila };
    }

    if (dateFilter === "week") {
        const weekdayName = new Intl.DateTimeFormat("en-US", { timeZone: APP_TIMEZONE, weekday: "long" }).format(now);
        const weekdayIndex = {
            Sunday: 0,
            Monday: 1,
            Tuesday: 2,
            Wednesday: 3,
            Thursday: 4,
            Friday: 5,
            Saturday: 6,
        }[weekdayName] ?? 1;
        const daysSinceMonday = (weekdayIndex + 6) % 7;
        const start = shiftDateOnlyByDaysUtc(todayManila, daysSinceMonday);
        return { startDate: start, endDate: todayManila };
    }

    if (dateFilter === "month") {
        const [year, month] = todayManila.split("-");
        const start = `${year}-${month}-01`;
        return { startDate: start, endDate: todayManila };
    }

    return { startDate: "", endDate: "" };
};

function TransactionList({ onSelect, restrictToToday = false, showSummary = true, useExpensesPagination = true }) {
    // Get logged-in user info from Redux
    const user = useSelector(state => state.auth.user);
    const customer_id = user?.customer_id;
    const branch_id = user?.branch_id;
    const user_id = user?.id;
    // All useState declarations at the top
    const [transactions, setTransactions] = useState([]);
    const [stockMovements, setStockMovements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState(() => (restrictToToday ? "grid" : "summary"));
    const [dateFilter, setDateFilter] = useState("today");
    const currentYear = Number(formatDateInTimeZone(new Date()).slice(0, 4));
    const [selectedYear, setSelectedYear] = useState(() => String(currentYear));
    const [selectedDate, setSelectedDate] = useState("");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [paymentFilter, setPaymentFilter] = useState("");
    const [orderSearch, setOrderSearch] = useState("");
    const [cashierFilter, setCashierFilter] = useState("");
    const [selectedTx, setSelectedTx] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [voidTx, setVoidTx] = useState(null);
    const [voiding, setVoiding] = useState(false);
    const [voidPassword, setVoidPassword] = useState("");
    const [voidPasswordError, setVoidPasswordError] = useState("");
    const [voidAuthorizerIdentifier, setVoidAuthorizerIdentifier] = useState("");
    const [voidAuthorizedRole, setVoidAuthorizedRole] = useState("manager");
    const [branches, setBranches] = useState([]);
    const [users, setUsers] = useState([]);
    const [branchFilter, setBranchFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalExpenses, setTotalExpenses] = useState(0);
    const listScrollRef = useRef(null);
    const pageSize = 20;
    const filteredCashierUsers = useMemo(() => {
        if (!branchFilter) return users || [];
        return (users || []).filter((u) => {
            const userBranchId = u.branch_id ?? u.branch?.id ?? "";
            return String(userBranchId) === String(branchFilter);
        });
    }, [users, branchFilter]);

    // Fetch transactions on mount and when filters change
    useEffect(() => {
        if (dateFilter === "custom" && (!customStartDate || !customEndDate)) {
            setTransactions([]);
            setStockMovements([]);
            setError(null);
            setLoading(false);
            return;
        }

        if (dateFilter === "selectDate" && !selectedDate) {
            setTransactions([]);
            setStockMovements([]);
            setError(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        console.log('[TransactionList] Filters:', { branchFilter, dateFilter });
        const token = sessionStorage.getItem("token") || localStorage.getItem("token");

        // Build params with filters - Best Practice: Server-side filtering
        let params = {};

        if (restrictToToday && customer_id && branch_id) {
            // Cashier view: fixed branch and today only
            params = { customer_id, branch_id };
            if (user_id) params.user_id = user_id;
        } else {
            // Admin view: flexible filtering
            if (customer_id) params.customer_id = customer_id;
            if (branchFilter) params.branch_id = Number(branchFilter);
            if (cashierFilter) params.user_id = Number(cashierFilter);
        }

        // Add status filter (both admin and cashier)
        if (statusFilter) params.status = statusFilter;

        // Add date filter (admin and cashier)
        if (dateFilter) {
            const usesDateRange = ["custom", "year", "selectYear", "selectDate"].includes(dateFilter);
            params.dateFilter = usesDateRange ? "custom" : dateFilter;
            if (usesDateRange) {
                const { startDate, endDate } = getManilaDateRange({ dateFilter, customStartDate, customEndDate, selectedYear, selectedDate });
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
            }
        }

        axiosInstance.get("admin/orders", {
            params,
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                console.log('[TransactionList] API response:', res.data);
                const transactionList = Array.isArray(res.data) ? res.data : res.data?.orders || [];
                const movementList = Array.isArray(res.data?.stockMovements) ? res.data.stockMovements : [];
                setTransactions(transactionList);
                setStockMovements(movementList);
                setError(null);
            })
            .catch(() => {
                setError("Failed to load transactions");
            })
            .finally(() => setLoading(false));
    }, [restrictToToday, customer_id, branch_id, user_id, branchFilter, cashierFilter, statusFilter, dateFilter, customStartDate, customEndDate, selectedYear, selectedDate]);

    useEffect(() => {
        if (!customer_id) {
            setTotalExpenses(0);
            return;
        }

        if (dateFilter === "custom" && (!customStartDate || !customEndDate)) {
            setTotalExpenses(0);
            return;
        }

        if (dateFilter === "selectDate" && !selectedDate) {
            setTotalExpenses(0);
            return;
        }

        const { startDate, endDate } = getManilaDateRange({ dateFilter, customStartDate, customEndDate, selectedYear, selectedDate });
        const params = {};

        if (restrictToToday && branch_id) {
            params.branch_id = branch_id;
        } else {
            if (branchFilter) params.branch_id = branchFilter;
            if (cashierFilter) params.user_id = cashierFilter;
        }

        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;

        axiosInstance.get("/pos/expenses", { params })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                const total = list.reduce((sum, exp) => sum + Number(exp.amount || 0), 0);
                setTotalExpenses(total);
            })
            .catch(() => {
                setTotalExpenses(0);
            });
    }, [restrictToToday, customer_id, branch_id, branchFilter, cashierFilter, dateFilter, customStartDate, customEndDate, selectedYear, selectedDate]);

    useEffect(() => {
        if (dateFilter !== "custom") {
            setCustomStartDate("");
            setCustomEndDate("");
        }
        if (dateFilter !== "selectDate") {
            setSelectedDate("");
        }
    }, [dateFilter]);

    useEffect(() => {
        if (!cashierFilter) return;
        const cashierStillVisible = filteredCashierUsers.some((u) => String(u.id) === String(cashierFilter));
        if (!cashierStillVisible) {
            setCashierFilter("");
        }
    }, [cashierFilter, filteredCashierUsers]);

    // Fetch branches and users for filters
    useEffect(() => {
        if (!customer_id) return;
        fetchBranches(customer_id)
            .then(res => {
                setBranches(res.data.data);
            })
            .catch(() => {
                setBranches([]);
            });

        fetchUsers()
            .then(res => {
                setUsers(res.data.data || []);
            })
            .catch(() => {
                setUsers([]);
            });
    }, [customer_id]);

    useEffect(() => {
        if (!customer_id) return;
        axiosInstance.get("/user/workspace-preferences")
            .then((res) => {
                setVoidAuthorizedRole(normalizeVoidRole(res.data?.void_authorized_role));
            })
            .catch(() => {
                setVoidAuthorizedRole("manager");
            });
    }, [customer_id]);

    // Helper for relative time
    function getRelativeTime(dateString) {
        const now = new Date();
        const date = new Date(dateString);
        const diff = Math.floor((now - date) / 1000); // seconds
        if (diff < 60) return 'Just Now';
        if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} hrs ago`;
        return date.toLocaleDateString();
    }

    const baseFiltered = restrictToToday && user_id
        ? transactions.filter(tx => String(tx.user_id || tx.staff_id || tx.cashier_id) === String(user_id))
        : transactions;
    const filtered = useMemo(() => {
        const query = String(orderSearch || "").trim().toLowerCase().replace(/^#/, "");
        return baseFiltered.filter((tx) => {
            if (paymentFilter === "cash" && !isCashPayment(tx)) return false;
            if (paymentFilter === "online" && isCashPayment(tx)) return false;
            if (!query) return true;
            const orderNumber = String(tx.order_number || "").toLowerCase();
            const normalizedOrder = orderNumber.replace(/^#/, "");
            return orderNumber.includes(query) || normalizedOrder.includes(query);
        });
    }, [baseFiltered, orderSearch, paymentFilter]);
    const paidFiltered = useMemo(
        () => filtered.filter((tx) => String(tx.status || "").toUpperCase() === "PAID"),
        [filtered]
    );
    const isCashierRequester = Number(user?.user_type) === 2 && getUserRoleName(user) === "cashier";
    const matchingVoidAuthorizers = useMemo(() => {
        const policy = normalizeVoidRole(voidAuthorizedRole);
        return (users || [])
            .filter((candidate) => String(candidate?.status || "").toUpperCase() === "ACTIVE")
            .filter((candidate) => userMatchesVoidPolicy(candidate, policy));
    }, [users, voidAuthorizedRole]);
    const shouldEnterVoidAuthorizer = isCashierRequester && matchingVoidAuthorizers.length > 1;

    const openVoidModal = (tx) => {
        setVoidPassword("");
        setVoidPasswordError("");
        setVoidAuthorizerIdentifier("");
        setVoidTx(tx);
    };

    const handleReprintReceipt = async (tx) => {
        try {
            await printReceipt(tx);
            showSuccessToast("Receipt sent to printer.");
        } catch (error) {
            showErrorToast(error.response?.data?.message || "Receipt printing failed.");
        }
    };

    const closeVoidModal = () => {
        if (voiding) return;
        setVoidTx(null);
        setVoidPassword("");
        setVoidPasswordError("");
        setVoidAuthorizerIdentifier("");
    };

    const handleVoidOrder = async () => {
        if (!voidTx || voiding) return;
        const password = voidPassword;
        if (shouldEnterVoidAuthorizer && !voidAuthorizerIdentifier.trim()) {
            setVoidPasswordError(`Enter the ${getVoidRoleLabel(voidAuthorizedRole)} username or email authorizing this void.`);
            return;
        }
        if (!password.trim()) {
            setVoidPasswordError("Enter your password to continue.");
            return;
        }

        setVoidPasswordError("");
        setVoiding(true);
        try {
            const response = await axiosInstance.patch(`admin/orders/${voidTx.id}/void`, {
                password,
                authorizer_username_or_email: shouldEnterVoidAuthorizer ? voidAuthorizerIdentifier.trim() : null,
            });
            const updatedOrder = response.data?.order || response.data;

            setTransactions((prev) => prev.map((tx) => (
                String(tx.id) === String(updatedOrder.id)
                    ? {
                        ...tx,
                        ...updatedOrder,
                        products: updatedOrder.products || tx.products,
                        branch_id: tx.branch_id,
                        branch_name: tx.branch_name,
                        cashier_name: tx.cashier_name,
                    }
                    : tx
            )));

            setSelectedTx((prev) => {
                if (!prev || String(prev.id) !== String(updatedOrder.id)) return prev;
                return {
                    ...prev,
                    ...updatedOrder,
                    products: updatedOrder.products || prev.products,
                    branch_id: prev.branch_id,
                    branch_name: prev.branch_name,
                    cashier_name: prev.cashier_name,
                };
            });

            setVoidTx(null);
            setVoidPassword("");
            setVoidPasswordError("");
            setVoidAuthorizerIdentifier("");
            showSuccessToast(response.data?.message || "Order voided successfully.");
        } catch (error) {
            const message = error.response?.data?.message || "Failed to void order.";
            setVoidPasswordError(message);
            if (error.response?.status !== 400) {
                showErrorToast(message);
            }
        } finally {
            setVoiding(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [restrictToToday, customer_id, branch_id, user_id, branchFilter, cashierFilter, statusFilter, paymentFilter, orderSearch, dateFilter, customStartDate, customEndDate, selectedYear, viewMode]);

    const productSummary = useMemo(() => {
        const summaryMap = new Map();
        const scopeMap = new Map();

        paidFiltered.forEach((tx) => {
            const products = Array.isArray(tx.products) ? tx.products : [];
            const soldDate = formatDateInTimeZone(new Date(tx.created_at), APP_TIMEZONE);
            products.forEach((item) => {
                const qty = Number(item.qty || 0);
                const price = Number(item.price || 0);
                if (!qty) return;

                const name = item.name || "Unknown Item";
                const productKey = String(item.product_id || name);
                const key = `${soldDate}-${productKey}`;
                const existing = summaryMap.get(key) || {
                    key,
                    productKey,
                    date: soldDate,
                    name,
                    price,
                    prices: new Set(),
                    totalQty: 0,
                    stockIn: 0,
                    totalAmount: 0,
                    beginningStock: 0,
                    endingStock: 0,
                    snapshotBalances: new Map(),
                };

                existing.prices.add(price);
                if (!existing.price) existing.price = price;
                existing.totalQty += qty;
                existing.totalAmount += qty * price;
                summaryMap.set(key, existing);

                const branchId = item.branch_id || tx.branch_id || "unknown";
                const branchSnapshotKey = `${branchId}-${item.product_id || name}`;
                const stockBefore = item.stock_before;
                const stockAfter = item.stock_after;
                const hasStockSnapshot = stockBefore !== null
                    && stockBefore !== undefined
                    && stockAfter !== null
                    && stockAfter !== undefined;

                if (hasStockSnapshot) {
                    const timestamp = new Date(tx.created_at).getTime() || 0;
                    const branchSnapshot = existing.snapshotBalances.get(branchSnapshotKey) || {
                        firstTimestamp: timestamp,
                        lastTimestamp: timestamp,
                        stockBefore: Number(stockBefore),
                        stockAfter: Number(stockAfter),
                        stockInBeforeFirstSale: 0,
                        stockInAfterLastSale: 0,
                    };

                    if (timestamp <= branchSnapshot.firstTimestamp) {
                        branchSnapshot.firstTimestamp = timestamp;
                        branchSnapshot.stockBefore = Number(stockBefore);
                    }

                    if (timestamp >= branchSnapshot.lastTimestamp) {
                        branchSnapshot.lastTimestamp = timestamp;
                        branchSnapshot.stockAfter = Number(stockAfter);
                    }

                    existing.snapshotBalances.set(branchSnapshotKey, branchSnapshot);
                }

                if (!scopeMap.has(productKey)) {
                    scopeMap.set(productKey, {
                        branchStocks: new Map(),
                        groups: new Map(),
                    });
                }

                const scope = scopeMap.get(productKey);
                scope.groups.set(key, existing);

                const branchProductKey = `${branchId}-${item.product_id || name}`;
                if (!scope.branchStocks.has(branchProductKey)) {
                    scope.branchStocks.set(branchProductKey, Number(item.current_stock || 0));
                }
            });
        });

        stockMovements.forEach((movement) => {
            const qty = Number(movement.qty || 0);
            if (!qty) return;

            const stockInDate = movement.date;
            const productKey = String(movement.product_id || movement.name || "Unknown Item");
            const key = `${stockInDate}-${productKey}`;
            const existing = summaryMap.get(key);
            if (!existing) return;

            const movementTimestamp = new Date(movement.timestamp).getTime() || 0;
            const branchProductKey = `${movement.branch_id || "unknown"}-${movement.product_id || movement.name || "Unknown Item"}`;
            const branchSnapshot = existing.snapshotBalances.get(branchProductKey);

            if (existing.snapshotBalances.size > 0 && !branchSnapshot) return;

            if (branchSnapshot) {
                if (movementTimestamp <= branchSnapshot.firstTimestamp) {
                    branchSnapshot.stockInBeforeFirstSale += qty;
                } else if (movementTimestamp > branchSnapshot.lastTimestamp) {
                    branchSnapshot.stockInAfterLastSale += qty;
                }

                existing.snapshotBalances.set(branchProductKey, branchSnapshot);
            }

            existing.stockIn += qty;
            summaryMap.set(key, existing);
        });

        scopeMap.forEach((scope) => {
            let runningEndingStock = Array.from(scope.branchStocks.values())
                .reduce((sum, stock) => sum + Number(stock || 0), 0);

            const groups = Array.from(scope.groups.values())
                .sort((a, b) => b.date.localeCompare(a.date));

            groups.forEach((group) => {
                if (group.snapshotBalances.size > 0) {
                    const snapshots = Array.from(group.snapshotBalances.values());
                    group.beginningStock = snapshots.reduce((sum, snapshot) => {
                        return sum + Number(snapshot.stockBefore || 0) - Number(snapshot.stockInBeforeFirstSale || 0);
                    }, 0);
                    group.endingStock = snapshots.reduce((sum, snapshot) => {
                        return sum + Number(snapshot.stockAfter || 0) + Number(snapshot.stockInAfterLastSale || 0);
                    }, 0);
                    runningEndingStock = group.beginningStock;
                    return;
                }

                group.endingStock = runningEndingStock;
                group.beginningStock = runningEndingStock + group.totalQty - Number(group.stockIn || 0);
                runningEndingStock = group.beginningStock;
            });
        });

        return Array.from(summaryMap.values()).map(({ snapshotBalances, prices, ...item }) => ({
            ...item,
            priceLabel: prices?.size > 1 ? "Mixed" : item.price,
        })).sort((a, b) => {
            if (b.date !== a.date) return b.date.localeCompare(a.date);
            if (b.totalQty !== a.totalQty) return b.totalQty - a.totalQty;
            return b.totalAmount - a.totalAmount;
        });
    }, [paidFiltered, stockMovements]);

    const activeItems = viewMode === "summary" ? productSummary : filtered;
    const totalPages = Math.max(1, Math.ceil(activeItems.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const pagedTransactions = filtered.slice(startIndex, startIndex + pageSize);
    const pagedProductSummary = productSummary.slice(startIndex, startIndex + pageSize);
    const totalSales = paidFiltered.reduce((sum, tx) => sum + Number(tx.total || 0), 0);
    const cashSales = paidFiltered.reduce((sum, tx) => sum + (isCashPayment(tx) ? Number(tx.total || 0) : 0), 0);
    const onlineSales = Math.max(0, totalSales - cashSales);
    const totalItemsSold = productSummary.reduce((sum, item) => sum + item.totalQty, 0);
    const listPaddingBottom = "pb-4";
    const scrollSpacingBottom = activeItems.length > 0 ? "pb-2" : "pb-0";
    const handlePageChange = (page) => {
        const nextPage = Math.min(totalPages, Math.max(1, page));
        if (nextPage === safePage) return;

        setCurrentPage(nextPage);
        window.requestAnimationFrame(() => {
            listScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            listScrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    // Prepare filter configurations for FilterBar
    const filterConfigs = [
        {
            type: 'text',
            label: 'Search OR',
            value: orderSearch,
            onChange: setOrderSearch,
            placeholder: 'Enter OR number...',
            minWidth: '190px',
        },
        {
            type: 'react-select',
            label: 'Branch',
            value: (() => {
                if (!branchFilter) return { value: "", label: "All Branches" };
                const found = (branches || []).find(b => String(b.id) === branchFilter);
                return found ? { value: String(found.id), label: found.name } : { value: branchFilter, label: branchFilter };
            })(),
            onChange: setBranchFilter,
            options: [
                { value: "", label: "All Branches" },
                ...((Array.isArray(branches) ? branches : []).map(branch => ({ value: String(branch.id), label: branch.name })))
            ],
            isSearchable: true,
            placeholder: "All Branches",
            minWidth: '145px',
            hide: restrictToToday, // Hide for cashier view
        },
        {
            type: 'react-select',
            label: 'Date',
            value: (() => {
                const labelMap = {
                    today: "Today",
                    week: "This Week",
                    month: "This Month",
                    year: "This Year",
                    selectDate: "Select Date",
                    selectYear: "Select Year",
                    custom: "Custom Range",
                };
                const value = dateFilter || "today";
                return { value, label: labelMap[value] || value };
            })(),
            onChange: (value) => {
                setDateFilter(value);
                if (value === "selectDate") {
                    setSelectedDate((current) => current || formatDateInTimeZone(new Date(), APP_TIMEZONE));
                }
            },
            options: [
                { value: "today", label: "Today" },
                { value: "week", label: "This Week" },
                { value: "month", label: "This Month" },
                { value: "year", label: "This Year" },
                { value: "selectDate", label: "Select Date" },
                { value: "selectYear", label: "Select Year" },
                { value: "custom", label: "Custom Range" },
            ],
            isSearchable: true,
            placeholder: "Today",
            minWidth: '155px',
            hide: false,
        },
        {
            type: 'date',
            label: 'Selected Date',
            value: selectedDate,
            onChange: setSelectedDate,
            minWidth: '150px',
            hide: dateFilter !== "selectDate",
        },
        {
            type: 'react-select',
            label: 'Year',
            value: { value: selectedYear, label: selectedYear },
            onChange: setSelectedYear,
            options: Array.from({ length: currentYear - 2000 + 1 }, (_, index) => {
                const year = String(currentYear - index);
                return { value: year, label: year };
            }),
            isSearchable: true,
            minWidth: '120px',
            hide: dateFilter !== "selectYear",
        },
        {
            type: 'date',
            label: 'Start Date',
            value: customStartDate,
            onChange: setCustomStartDate,
            minWidth: '150px',
            hide: dateFilter !== "custom",
            max: customEndDate || undefined,
        },
        {
            type: 'date',
            label: 'End Date',
            value: customEndDate,
            onChange: setCustomEndDate,
            minWidth: '150px',
            hide: dateFilter !== "custom",
            min: customStartDate || undefined,
        },
        {
            type: 'react-select',
            label: 'Cashier',
            value: (() => {
                if (!cashierFilter) return { value: "", label: "All Cashiers" };
                const found = filteredCashierUsers.find(u => String(u.id) === cashierFilter)
                    || (users || []).find(u => String(u.id) === cashierFilter);
                return found ? { value: String(found.id), label: found.username || found.email } : { value: cashierFilter, label: cashierFilter };
            })(),
            onChange: setCashierFilter,
            options: [
                { value: "", label: "All Cashiers" },
                ...(filteredCashierUsers.map(user => ({
                    value: String(user.id),
                    label: user.username || user.email,
                })))
            ],
            isSearchable: true,
            placeholder: "All Cashiers",
            minWidth: '145px',
            hide: restrictToToday, // Hide for cashier view
        },
        {
            type: 'react-select',
            label: 'Status',
            value: (() => {
                if (!statusFilter) return { value: "", label: "All Status" };
                const labelMap = {
                    PAID: "Paid",
                    VOIDED: "Voided",
                };
                return { value: statusFilter, label: labelMap[statusFilter] || statusFilter };
            })(),
            onChange: setStatusFilter,
            options: [
                { value: "", label: "All Status" },
                { value: "PAID", label: "Paid" },
                { value: "VOIDED", label: "Voided" },
            ],
            isSearchable: true,
            placeholder: "All Status",
            minWidth: '120px',
        },
    ];

    filterConfigs.push({
        type: 'react-select',
        label: 'Payment',
        value: { value: paymentFilter, label: paymentFilter === "cash" ? "Cash" : paymentFilter === "online" ? "Online Payments" : "All Payments" },
        onChange: setPaymentFilter,
        options: [
            { value: "", label: "All Payments" },
            { value: "cash", label: "Cash" },
            { value: "online", label: "Online Payments" },
        ],
        placeholder: "All Payments",
        minWidth: '135px',
    });

    const gridToggleButton = (
        <button
            onClick={() => setViewMode("grid")}
            className={`p-2 rounded-xl ${viewMode === "grid" ? "text-white" : "bg-[#e5e5ea] text-gray-700"} transition-colors`}
            style={viewMode === "grid" ? { background: "var(--color-primary)" } : undefined}
            onMouseOver={viewMode === "grid" ? (event) => {
                event.currentTarget.style.background = "var(--color-hover)";
            } : undefined}
            onMouseOut={viewMode === "grid" ? (event) => {
                event.currentTarget.style.background = "var(--color-primary)";
            } : undefined}
            aria-label="Cards view"
        >
            <Grid size={18} />
        </button>
    );

    const summaryToggleButton = (
        <button
            onClick={() => setViewMode("summary")}
            className={`p-2 rounded-xl ${viewMode === "summary" ? "text-white" : "bg-[#e5e5ea] text-gray-700"} transition-colors`}
            style={viewMode === "summary" ? { background: "var(--color-primary)" } : undefined}
            onMouseOver={viewMode === "summary" ? (event) => {
                event.currentTarget.style.background = "var(--color-hover)";
            } : undefined}
            onMouseOut={viewMode === "summary" ? (event) => {
                event.currentTarget.style.background = "var(--color-primary)";
            } : undefined}
            aria-label="Product sales summary"
        >
            <List size={18} />
        </button>
    );

    // View toggle buttons. Admin defaults to summary and shows Cards as the second icon.
    const viewToggleActions = restrictToToday ? (
        <>
            {gridToggleButton}
            {summaryToggleButton}
        </>
    ) : (
        <>
            {summaryToggleButton}
            {gridToggleButton}
        </>
    );

    return (
        <div className="flex flex-col h-full min-h-0">
            {/* Minimalist Apple-inspired Filter Bar - Always visible */}
            <FilterBar compact filters={filterConfigs} extraActions={viewToggleActions} />

            {/* Loading State */}
            {loading && (
                <div className="flex-1 flex items-center justify-center">
                    <div>Loading transactions...</div>
                </div>
            )}

            {/* Error State */}
            {error && !loading && (
                <div className="flex-1 flex items-center justify-center">
                    <EmptyState
                        icon={ListOrdered}
                        title="Failed to Load Transactions"
                        message="Check your internet connection and try again."
                        onRetry={() => window.location.reload()}
                        retryLabel="Reload"
                    />
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && activeItems.length === 0 && (
                <div className="flex-1 flex items-center justify-center">
                    <EmptyState
                        icon={ListOrdered}
                        title={viewMode === "summary" ? "No Products Sold" : "No Transactions"}
                        message={viewMode === "summary" ? "No sold items match the selected filters." : "No transaction data available for the selected period."}
                    />
                </div>
            )}

            {/* Transaction List */}
            {!loading && !error && activeItems.length > 0 && (
                <div ref={listScrollRef} className={`flex-1 min-h-0 overflow-y-auto mt-2 ${scrollSpacingBottom}`}>
                    {viewMode === "summary" ? (
                        <div className={`overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm ${listPaddingBottom}`}>
                            <div className="min-w-[1060px]">
                                <div className="grid grid-cols-[110px,minmax(160px,1.7fr),130px,130px,120px,110px,100px,120px] gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-[11px] font-semibold uppercase text-gray-500">
                                    <div>Date</div>
                                    <div>Item Name</div>
                                    <div className="text-right">Beginning Stock</div>
                                    <div className="text-right">Added Stock</div>
                                    <div className="text-right">Count Sold</div>
                                    <div className="text-right">Ending Stock</div>
                                    <div className="text-right">Unit Price</div>
                                    <div className="text-right">Sales Total</div>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {pagedProductSummary.map((item) => {
                                        const unitPriceText = item.priceLabel === "Mixed"
                                            ? "Mixed"
                                            : `₱${Number(item.priceLabel || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

                                        return (
                                            <div
                                                key={item.key}
                                                className="grid grid-cols-[110px,minmax(160px,1.7fr),130px,130px,120px,110px,100px,120px] items-center gap-3 px-4 py-3 text-sm text-gray-700"
                                            >
                                                <div className="whitespace-nowrap text-gray-600">{formatDisplayDate(item.date)}</div>
                                                <div className="min-w-0 truncate font-medium text-gray-900" title={item.name}>{item.name}</div>
                                                <div className="flex justify-end">
                                                    <StockBadge value={item.beginningStock} tone="beginning" title="Beginning stock" />
                                                </div>
                                                <div className="flex justify-end">
                                                    <StockBadge value={item.stockIn} tone="added" title="Additional stocks" prefix="+" />
                                                </div>
                                                <div className="flex justify-end">
                                                    <StockBadge value={item.totalQty} tone="sold" title="Count sold" prefix="-" />
                                                </div>
                                                <div className="flex justify-end">
                                                    <StockBadge value={item.endingStock} tone="ending" title="Ending stock" prefix="= " />
                                                </div>
                                                <div className="text-right tabular-nums">
                                                    {unitPriceText}
                                                </div>
                                                <div className="text-right tabular-nums">
                                                    <div className="text-[11px] font-medium text-gray-400">
                                                        {item.totalQty.toLocaleString()} x {unitPriceText}
                                                    </div>
                                                    <div className="font-semibold text-gray-900">₱{item.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${listPaddingBottom}`}>
                            {pagedTransactions.map(tx => (
                                <TransactionCard
                                    key={tx.id}
                                    tx={tx}
                                    getRelativeTime={getRelativeTime}
                                    onVoid={() => openVoidModal(tx)}
                                    onClick={() => {
                                        setSelectedTx(tx);
                                        setModalOpen(true);
                                        if (onSelect) onSelect(tx.id);
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!loading && !error && filtered.length > 0 && showSummary && (
                <div className="mt-4 rounded-xl bg-white p-3 shadow-md ring-1 ring-gray-100">
                    <div className="grid grid-cols-2 gap-1.5 text-[10px] text-gray-700 sm:grid-cols-4 sm:gap-2 sm:text-xs">
                        <div className="rounded-lg bg-gray-50 px-2 py-1.5 sm:px-3 sm:py-2">
                            <div className="text-[9px] font-medium uppercase tracking-tight text-gray-500 sm:text-[10px] sm:tracking-wide">{viewMode === "summary" ? "Items Sold" : "Total Count"}</div>
                            <div className="mt-0.5 text-xs font-semibold text-gray-900 sm:text-sm">{viewMode === "summary" ? totalItemsSold.toLocaleString() : filtered.length}</div>
                        </div>
                        <div className="rounded-lg bg-gray-50 px-2 py-1.5 sm:px-3 sm:py-2">
                            <div className="text-[9px] font-medium uppercase tracking-tight text-gray-500 sm:text-[10px] sm:tracking-wide">Cash to Remit</div>
                            <div className="mt-0.5 text-xs font-semibold text-gray-900 sm:text-sm">₱{cashSales.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="rounded-lg bg-gray-50 px-2 py-1.5 sm:px-3 sm:py-2">
                            <div className="text-[9px] font-medium uppercase tracking-tight text-gray-500 sm:text-[10px] sm:tracking-wide">Online Payments</div>
                            <div className="mt-0.5 text-xs font-semibold text-gray-900 sm:text-sm">₱{onlineSales.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="rounded-lg bg-gray-50 px-2 py-1.5 sm:px-3 sm:py-2">
                            <div className="text-[9px] font-medium uppercase tracking-tight text-gray-500 sm:text-[10px] sm:tracking-wide">Total Expenses</div>
                            <div className="mt-0.5 text-xs font-semibold text-gray-900 sm:text-sm">₱{totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                    </div>
                </div>
            )}

            {!loading && !error && activeItems.length > 0 && useExpensesPagination && (
                <div className="mt-4 flex items-center justify-between gap-3 overflow-x-auto whitespace-nowrap">
                    <div className="shrink-0 text-xs text-gray-500">
                        Showing {startIndex + 1}-{Math.min(startIndex + pageSize, activeItems.length)} of {activeItems.length}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                            onClick={() => handlePageChange(safePage - 1)}
                            disabled={safePage === 1}
                        >
                            Prev
                        </button>
                        <div className="text-xs text-gray-600">
                            Page {safePage} of {totalPages}
                        </div>
                        <button
                            type="button"
                            className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                            onClick={() => handlePageChange(safePage + 1)}
                            disabled={safePage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                {selectedTx ? (
                    <div className="block max-h-[86dvh] w-full overflow-x-hidden overflow-y-auto rounded-[28px] bg-white md:grid md:grid-cols-[minmax(0,1fr)_320px]">
                        <div className="min-w-0 bg-slate-50">
                            <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-5 sm:px-6">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Transaction Details</p>
                                <div className="mt-2 flex flex-col gap-2 pr-12">
                                    <h2 className="break-all text-xl font-bold leading-tight text-slate-950">Order #{selectedTx.order_number}</h2>
                                    <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
                                            {new Date(selectedTx.created_at).toLocaleDateString()} {new Date(selectedTx.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">
                                            {selectedTx.status}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="px-5 py-4 sm:px-6">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <p className="text-sm font-semibold text-slate-900">Items</p>
                                    <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-600 shadow-sm">
                                        {(selectedTx.products || []).reduce((sum, item) => sum + (item.qty || 0), 0)} qty
                                    </span>
                                </div>
                                {selectedTx.products && selectedTx.products.length > 0 ? (
                                    <div className="space-y-2">
                                        {selectedTx.products.map((item, idx) => (
                                            <div key={idx} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">
                                                    {idx + 1}
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <p className="break-words text-sm font-semibold text-slate-900">{item.name || "Item"}</p>
                                                    <p className="text-xs font-medium text-slate-500">Qty {item.qty || 1}</p>
                                                </div>
                                                <span className="rounded-full bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                                                    x{item.qty || 1}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                                        No items recorded for this transaction.
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex min-w-0 flex-col justify-between border-t border-slate-200 bg-white p-5 md:border-l md:border-t-0">
                            <div className="space-y-4">
                                <div
                                    className="rounded-2xl p-4 text-white shadow-[0_16px_38px_rgba(15,23,42,0.14)]"
                                    style={{
                                        background: "linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 78%, black))",
                                    }}
                                >
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "rgba(255,255,255,0.72)" }}>Total Paid</p>
                                    <p className="mt-1 text-3xl font-bold leading-none" style={{ color: "#fff" }}>{formatCurrency(selectedTx.total)}</p>
                                    <div className="mt-4 rounded-xl bg-white/12 px-3 py-2 text-xs">
                                        <div className="flex justify-between">
                                            <span style={{ color: "rgba(255,255,255,0.78)" }}>Payment</span>
                                            <span className="font-semibold capitalize" style={{ color: "#fff" }}>{selectedTx.payment_method || "-"}</span>
                                        </div>
                                        <div className="mt-1 flex justify-between">
                                            <span style={{ color: "rgba(255,255,255,0.78)" }}>Status</span>
                                            <span className="font-semibold" style={{ color: "#fff" }}>{selectedTx.status || "-"}</span>
                                        </div>
                                        {selectedTx.payment_collection_mode === "MANUAL_ONLINE" && selectedTx.payment_reference_last4 && (
                                            <div className="mt-1 flex justify-between">
                                                <span style={{ color: "rgba(255,255,255,0.78)" }}>Manual Ref</span>
                                                <span className="font-semibold" style={{ color: "#fff" }}>•••• {selectedTx.payment_reference_last4}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
                                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Order Info</p>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between gap-3">
                                            <span className="text-slate-500">Cashier</span>
                                            <span className="font-semibold text-slate-900">{selectedTx.cashier_name || selectedTx.cashier || selectedTx.user_name || '-'}</span>
                                        </div>
                                        <div className="flex justify-between gap-3">
                                            <span className="text-slate-500">Seat Number</span>
                                            <span className="font-semibold text-slate-900">{selectedTx.seat_number || '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                {String(selectedTx.payment_method || "").toLowerCase() === "cash" && selectedTx.amount_received !== null && selectedTx.amount_received !== undefined && (
                                    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Amount Received</span>
                                            <span className="font-bold text-slate-900">{formatCurrency(selectedTx.amount_received)}</span>
                                        </div>
                                        <div className="mt-2 flex justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm">
                                            <span className="font-medium text-slate-500">Change</span>
                                            <span className="font-bold text-slate-900">{formatCurrency(selectedTx.change_amount || 0)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-5 grid gap-2 sm:grid-cols-2">
                                {String(selectedTx.status || "").toUpperCase() === "PAID" && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => handleReprintReceipt(selectedTx)}
                                            className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100 focus:outline-none"
                                        >
                                            <Printer size={16} />
                                            Reprint
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => openVoidModal(selectedTx)}
                                            className="h-12 rounded-xl border border-rose-200 bg-rose-50 px-4 text-sm font-semibold text-rose-700 shadow-sm transition hover:bg-rose-100 focus:outline-none"
                                        >
                                            Void Order
                                        </button>
                                    </>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    className={`h-12 rounded-xl px-4 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 focus:outline-none ${String(selectedTx.status || "").toUpperCase() === "PAID" ? "sm:col-span-2" : ""}`}
                                    style={{ background: "var(--color-primary)" }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </Modal>

            <ConfirmationModal
                isOpen={Boolean(voidTx)}
                onClose={closeVoidModal}
                onConfirm={handleVoidOrder}
                title="Void Order"
                message={`Void order #${voidTx?.order_number || ""}? Ask an admin owner or authorized ${getVoidRoleLabel(voidAuthorizedRole)} account holder to enter their login password.`}
                confirmText={voiding ? "Voiding..." : "Void Order"}
                cancelText="Cancel"
                type="delete"
                confirmDisabled={voiding || !voidPassword.trim() || (shouldEnterVoidAuthorizer && !voidAuthorizerIdentifier.trim())}
            >
                {shouldEnterVoidAuthorizer && (
                    <div className="mb-4">
                        <label className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                            Username or Email
                        </label>
                        <input
                            type="text"
                            value={voidAuthorizerIdentifier}
                            onChange={(event) => {
                                setVoidAuthorizerIdentifier(event.target.value);
                                if (voidPasswordError) setVoidPasswordError("");
                            }}
                            autoComplete="username"
                            placeholder={`Enter ${getVoidRoleLabel(voidAuthorizedRole)} username or email`}
                            className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
                        />
                    </div>
                )}
                <label className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                    Login Password
                </label>
                <input
                    type="text"
                    value={voidPassword}
                    onChange={(event) => {
                        setVoidPassword(event.target.value);
                        if (voidPasswordError) setVoidPasswordError("");
                    }}
                    autoFocus
                    autoComplete="new-password"
                    name={`void-order-password-${voidTx?.id || "confirm"}`}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    placeholder="Enter your password"
                    className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
                    style={{ WebkitTextSecurity: "disc" }}
                />
                <p className="mt-2 text-xs leading-5 text-slate-500">
                    The order will be marked voided, stock will be restored, and your user name will be recorded.
                </p>
                {voidPasswordError && (
                    <p className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
                        {voidPasswordError}
                    </p>
                )}
            </ConfirmationModal>
        </div>
    );
}

export default TransactionList;
